import { IPE_WORKFLOW_STEP_CATALOG } from './ipeWorkflowSteps';
import { TOOL_NAV_ITEMS } from './workbenchNavConfig';
import { rewriteLegacyGoodsStudioUserCopy } from '@/pages/ipe/workbench/utils/workflowLegacyCopy';

/**
 * 流水线已实现 runner 的步骤。与画布目录取并集，避免「有执行器但智能工具分区漏登」再次拒跑。
 * 画布是否展示仍由 IPE_WORKFLOW_STEP_CATALOG 决定。
 */
export const WORKFLOW_ALWAYS_EXECUTABLE_NAV_IDS = Object.freeze([
  'stampExtract',
]);

export const WORKFLOW_EXECUTABLE_NAV_IDS = new Set([
  ...IPE_WORKFLOW_STEP_CATALOG.map((s) => s.navId).filter((id) => id !== 'sourcingHub'),
  ...WORKFLOW_ALWAYS_EXECUTABLE_NAV_IDS,
]);

const LABEL_TO_NAV_FROM_TOOLS = Object.values(TOOL_NAV_ITEMS).reduce((acc, item) => {
  if (item?.label && item?.id) acc[item.label] = item.id;
  return acc;
}, {});

const LABEL_TO_NAV = {
  ...LABEL_TO_NAV_FROM_TOOLS,
  插件采集: 'sourcingHub',
  图案裁剪: 'imageCrop',
  图片裁剪: 'imageCrop',
  纠偏裁剪: 'imageCrop',
  网图裁剪: 'imageCrop',
  图变裁剪: 'imageCrop',
  一键抠图: 'smartCutout',
  智能抠图: 'smartCutout',
  超分提质: 'imageEnhance',
  无损放大: 'imageEnhance',
  印花图提取: 'stampExtract',
  印花提取: 'stampExtract',
  主图创作: 'goods',
  主图创建: 'goods',
  AI组图: 'goods',
  AI组图产出: 'goods',
  AI智能组图: 'goods',
  'AI 智能组图': 'goods',
  商品套图: 'podProductDesign',
  POD商品设计: 'podProductDesign',
  商品抠图: 'goods',
  素材重绘: 'goods',
  标题提取: 'aiCopywriting',
  标题模板: 'aiCopywriting',
  侵权检测: 'infringementDetection',
  批量上架: 'platformListing',
  商品库: 'platformListing',
  商品上架: 'platformListing',
  刊登任务: 'platformListing',
};

/** 工作台「页面」nav → 流水线「算子」nav。队列/监控页不是独立步骤。 */
export const WORKFLOW_PAGE_NAV_TO_STEP = {
  platformListingJobs: 'platformListing',
  /**
   * 「定时上架」与「批量上架」共用 platformListing 算子：
   * 参数里带 listingAutoRuleId（定时/间隔上架自动规则）时，刊登接口会自动路由到定时库。
   * 之前它没做映射，画布节点落到未登记步骤，任务必然报「执行后未写入有效结果」。
   */
  platformScheduledListings: 'platformListing',
};

const NAV_ALIASES = {
  cutout: 'smartCutout',  smart_cutout: 'smartCutout',
  stamp: 'stampExtract',
  stamp_extract: 'stampExtract',
  stampextract: 'stampExtract',
  enhance: 'imageEnhance',
  image_enhance: 'imageEnhance',
  crop: 'imageCrop',
  image_crop: 'imageCrop',
  goods: 'goods',
  productcomposite: 'podProductDesign',
  product_composite: 'podProductDesign',
  titles: 'aiCopywriting',
  title: 'aiCopywriting',
  titleextract: 'aiCopywriting',
  title_extract: 'aiCopywriting',
  infringement: 'infringementDetection',
  infringement_detection: 'infringementDetection',
  listing: 'platformListing',
  platform_listing: 'platformListing',
  platformlistingjobs: 'platformListing',
  platform_listing_jobs: 'platformListing',
  listingjobs: 'platformListing',
  listing_jobs: 'platformListing',
  platform_scheduled_listings: 'platformListing',
  scheduled_listing: 'platformListing',
};

export function canonicalizeWorkflowNavId(navId) {
  const id = String(navId || '').trim();
  if (!id) return '';
  return WORKFLOW_PAGE_NAV_TO_STEP[id] || WORKFLOW_PAGE_NAV_TO_STEP[id.toLowerCase()] || id;
}

function resolveNavIdString(raw, label = '') {
  const id = String(raw || '').trim();
  if (!id && label && LABEL_TO_NAV[label]) {
    return canonicalizeWorkflowNavId(LABEL_TO_NAV[label]);
  }
  if (!id) return '';
  const canonical = canonicalizeWorkflowNavId(id);
  if (WORKFLOW_EXECUTABLE_NAV_IDS.has(canonical) || canonical === 'sourcingHub') return canonical;
  const lower = canonical.toLowerCase();
  if (NAV_ALIASES[lower]) return NAV_ALIASES[lower];
  if (LABEL_TO_NAV[id]) return canonicalizeWorkflowNavId(LABEL_TO_NAV[id]);
  if (LABEL_TO_NAV[canonical]) return canonicalizeWorkflowNavId(LABEL_TO_NAV[canonical]);
  return canonical;
}

/** 标签与 navId 不一致时优先信标签（修复旧模板「一键抠图」却写成 stampExtract 等） */
export function reconcileWorkflowStepNavId(step) {
  if (!step || typeof step !== 'object') return '';
  const label = String(step.label || '').trim();
  const labelNav = label && LABEL_TO_NAV[label]
    ? canonicalizeWorkflowNavId(LABEL_TO_NAV[label])
    : '';
  const fromNav = resolveNavIdString(String(step.navId || '').trim(), label);
  if (labelNav && fromNav && labelNav !== fromNav) {
    console.warn(
      `[工作流模板] 步骤「${label}」的 navId(${fromNav}) 与标签不一致，已按标签纠正为 ${labelNav}`,
    );
    return labelNav;
  }
  return fromNav || labelNav || '';
}

/** 将模板步骤 / 旧数据中的 navId、label 规范为流水线可识别的 navId */
export function resolveWorkflowNavId(stepOrNavId, labelHint = '') {
  if (typeof stepOrNavId === 'string') {
    return resolveNavIdString(stepOrNavId, labelHint);
  }
  const step = stepOrNavId;
  if (!step || typeof step !== 'object') return '';
  const reconciled = reconcileWorkflowStepNavId(step);
  if (reconciled) return reconciled;
  const label = String(step.label || labelHint || '').trim();
  const fromNav = resolveNavIdString(String(step.navId || '').trim(), label);
  if (fromNav && (WORKFLOW_EXECUTABLE_NAV_IDS.has(fromNav) || fromNav === 'sourcingHub')) {
    return fromNav;
  }
  if (label && LABEL_TO_NAV[label]) return LABEL_TO_NAV[label];
  return fromNav;
}

/** 步骤是否可自动执行（sourcingHub 是素材采集入口，允许存在但不参与执行） */
export function isWorkflowStepExecutable(stepOrNavId) {
  const navId = resolveWorkflowNavId(stepOrNavId);
  if (!navId) return false;
  if (navId === 'sourcingHub') return true;
  return WORKFLOW_EXECUTABLE_NAV_IDS.has(navId);
}

/**
 * 条目在侵权等步骤被用户终止时，强制步骤条展示：终止步=stopped，后续保持待执行。
 */
export function buildWorkflowRowStepDisplay(pipelineSteps, result, emptyFactory) {
  const display = mapPipelineResultSteps(pipelineSteps, result?.steps || [], emptyFactory);
  if (!result?.skipped) return display;

  let stopIdx = pipelineSteps.findIndex(
    (ps) => resolveWorkflowNavId(ps) === (result.stoppedAtNavId || 'infringementDetection'),
  );
  if (stopIdx < 0) {
    const skippedStep = (result.steps || []).find((s) => s?.status === 'skipped' || s?.status === 'stopped');
    if (skippedStep) {
      const nav = resolveWorkflowNavId(skippedStep);
      stopIdx = pipelineSteps.findIndex((ps) => resolveWorkflowNavId(ps) === nav);
    }
  }
  if (stopIdx < 0) return display;

  const stopDetail = result.error || '已按您的选择终止当前条目';
  return display.map((st, i) => {
    if (i < stopIdx) return st;
    if (i === stopIdx) {
      return { ...st, status: st.status === 'failed' ? 'failed' : 'stopped', detail: stopDetail };
    }
    return {
      ...st,
      status: 'pending',
      detail: '',
    };
  });
}

/** 将流水线结果 steps 按 navId 对齐到模板步骤顺序（避免下标错位导致中间步显示为跳过） */
export function mapPipelineResultSteps(pipelineSteps, resultSteps, emptyFactory) {
  const empty = emptyFactory || (() => ({
    status: 'pending',
    detail: '',
    inputImageUrl: '',
    outputImageUrl: '',
    previewLeftLabel: '',
    previewCenterLabel: '',
    previewRightLabel: '',
    modelReferenceImageUrl: '',
    modelReferencePresetId: '',
    modelReferenceModelType: '',
    modelReferenceGarmentType: '',
    modelReferenceUsedUploadedModel: false,
    outputVideoUrl: '',
    outputVideoUrls: [],
  }));
  if (!Array.isArray(pipelineSteps) || pipelineSteps.length === 0) return [];
  if (!Array.isArray(resultSteps) || resultSteps.length === 0) {
    return pipelineSteps.map(() => empty());
  }
  const byNav = new Map();
  resultSteps.forEach((rec) => {
    if (!rec) return;
    const key = resolveWorkflowNavId(rec) || rec.navId;
    if (!key) return;
    if (!byNav.has(key)) byNav.set(key, []);
    byNav.get(key).push(rec);
  });
  return pipelineSteps.map((ps, i) => {
    const navId = resolveWorkflowNavId(ps);
    const atIndex = resultSteps[i] || null;
    let rec = null;
    // 优先按位次匹配；否则按 navId 取「尚未被消费」的第一条。
    // 避免模板里存在两个同名 nav（如 titles 与 aiCopywriting 归一后同名）时后者覆盖前者。
    if (atIndex && (!navId || (resolveWorkflowNavId(atIndex) || atIndex.navId) === navId)) {
      rec = atIndex;
    } else {
      const queue = navId ? byNav.get(navId) : null;
      rec = (queue && queue.length ? queue[0] : null) || atIndex;
    }
    if (rec) {
      const key = resolveWorkflowNavId(rec) || rec.navId;
      const queue = key ? byNav.get(key) : null;
      if (queue) {
        const qi = queue.indexOf(rec);
        if (qi >= 0) queue.splice(qi, 1);
      }
    }
    if (!rec) return empty();
    let status = 'pending';
    if (rec.status === 'ok') {
      const nav = resolveWorkflowNavId(rec);
      const isHigh =
        Boolean(rec.highRisk) ||
        String(rec.riskLevel || '').toLowerCase() === 'high' ||
        (nav === 'infringementDetection' && /高风险|较高侵权|侵权风险/i.test(String(rec.note || '')));
      status = isHigh ? 'risk' : 'done';
    } else if (rec.status === 'failed') status = 'failed';
    else if (rec.status === 'stopped') status = 'stopped';
    else if (rec.status === 'skipped') status = 'skipped';
    return {
      status,
      detail: rec.error || rec.note || (
        navId === 'aiCopywriting'
          ? String(rec.copywritingText || rec.titleText || '').trim()
          : ''
      ) || '',
      riskLevel: rec.riskLevel || '',
      highRisk: Boolean(rec.highRisk),
      inputImageUrl: rec.inputImageUrl || '',
      outputImageUrl: rec.outputImageUrl || '',
      previewLeftLabel: rewriteLegacyGoodsStudioUserCopy(rec.previewLeftLabel || ''),
      previewCenterLabel: rewriteLegacyGoodsStudioUserCopy(rec.previewCenterLabel || ''),
      previewRightLabel: rewriteLegacyGoodsStudioUserCopy(rec.previewRightLabel || ''),
      modelReferenceImageUrl: rec.modelReferenceImageUrl || '',
      modelReferencePresetId: rec.modelReferencePresetId || '',
      modelReferenceModelType: rec.modelReferenceModelType || '',
      modelReferenceGarmentType: rec.modelReferenceGarmentType || '',
      modelReferenceUsedUploadedModel: rec.modelReferenceUsedUploadedModel === true,
      copywritingText: rec.copywritingText || rec.titleText || '',
      outputVideoUrl: rec.outputVideoUrl || '',
      outputVideoUrls: Array.isArray(rec.outputVideoUrls) ? rec.outputVideoUrls : [],
      ...pickWorkflowStepLinkFields(rec),
    };
  });
}

/** 步骤是否已有可展示的成功产出（用于重试时定位续跑步骤） */
export function isWorkflowStepRecordComplete(rec, navId) {
  if (!rec) return false;
  if (rec.status === 'failed' || rec.status === 'stopped') return false;
  if (rec.status === 'ok') return true;
  const nav = navId || resolveWorkflowNavId(rec);
  if (nav === 'aiCopywriting' && String(rec.copywritingText || rec.titleText || '').trim()) return true;
  if ((nav === 'videos' || nav === 'textToVideo') && String(rec.outputVideoUrl || '').trim()) return true;
  if ((nav === 'videos' || nav === 'textToVideo') && Array.isArray(rec.outputVideoUrls) && rec.outputVideoUrls.length > 0) return true;
  if (nav === 'infringementDetection' && (rec.note || rec.riskLevel)) return true;
  if (nav === 'platformListing' && String(rec.note || '').trim()) return true;
  if (Array.isArray(rec.compositeImageUrls) && rec.compositeImageUrls.length > 0) return true;
  if (String(rec.outputImageUrl || '').trim()) return true;
  return false;
}

/** 重试条目时从第一个未完成/失败步骤继续，避免把已成功步骤一并重跑 */
export function findFirstRetryWorkflowStepIndex(result, pipelineSteps) {
  if (!result || !Array.isArray(pipelineSteps) || pipelineSteps.length === 0) return 0;
  for (let i = 0; i < pipelineSteps.length; i += 1) {
    const step = pipelineSteps[i];
    const navId = resolveWorkflowNavId(step);
    const rec = findPipelineResultStep(result.steps, navId, i);
    if (rec?.status === 'failed' || rec?.status === 'stopped') return i;
    if (!isWorkflowStepRecordComplete(rec, navId)) return i;
  }
  return Math.max(0, pipelineSteps.length - 1);
}

const defaultEmptyStepDisplay = () => ({
  status: 'pending',
  detail: '',
  inputImageUrl: '',
  outputImageUrl: '',
  previewLeftLabel: '',
  previewCenterLabel: '',
  previewRightLabel: '',
  modelReferenceImageUrl: '',
  modelReferencePresetId: '',
  modelReferenceModelType: '',
  modelReferenceGarmentType: '',
  modelReferenceUsedUploadedModel: false,
  outputVideoUrl: '',
  outputVideoUrls: [],
});

/** 重试/审核续跑：保留 resumeStep 之前已完成步骤的缩略图与状态 */
export function buildPreservedWorkflowStepDisplay({
  pipelineSteps,
  priorEntry,
  resumeStep,
  emptyFactory,
}) {
  const empty = emptyFactory || defaultEmptyStepDisplay;
  const safeResume = Math.max(0, Number(resumeStep) || 0);
  if (!Array.isArray(pipelineSteps) || pipelineSteps.length === 0) return [];
  if (safeResume <= 0) return pipelineSteps.map(() => empty());

  const priorStepDisplay = priorEntry?.stepDisplay;
  const priorResult = priorEntry?.result;
  const preservedSteps = Array.isArray(priorResult?.steps) && priorResult.steps.length > 0
    ? priorResult.steps.slice(0, safeResume)
    : [];

  let baseDisplay = [];
  if (Array.isArray(priorStepDisplay) && priorStepDisplay.length > 0) {
    baseDisplay = priorStepDisplay;
  } else if (preservedSteps.length > 0) {
    baseDisplay = buildWorkflowRowStepDisplay(
      pipelineSteps,
      { steps: preservedSteps },
      empty,
    );
  }

  return pipelineSteps.map((_, i) => {
    if (i >= safeResume) return empty();
    const preserved = baseDisplay[i];
    if (preserved?.status && preserved.status !== 'pending') return preserved;
    if (preserved?.outputImageUrl || preserved?.detail) return preserved;
    return empty();
  });
}

/** 运行中合并已存储步骤与 live 进度，避免重试时把已完成步骤显示为空 */
export function mergeWorkflowStepDisplay(stored, live, stepCount) {
  const len = stepCount || Math.max(stored?.length || 0, live?.length || 0);
  const empty = defaultEmptyStepDisplay();
  return Array.from({ length: len }, (_, i) => {
    const st = stored?.[i];
    const lt = live?.[i];
    if (lt?.status === 'running') return lt;
    if (lt?.status && lt.status !== 'pending' && (lt.outputImageUrl || lt.outputVideoUrl || lt.detail || lt.inputImageUrl)) {
      return lt;
    }
    if (st?.status && st.status !== 'pending' && (st.outputImageUrl || st.outputVideoUrl || st.detail || st.inputImageUrl)) {
      return st;
    }
    return lt || st || empty;
  });
}

/** 条目级结果：success | partial | failed | pending | review */
export function classifyWorkflowRowOutcome(result, pipelineSteps) {
  if (!result || result.inProgress) return 'pending';
  if (result.pendingReview) return 'review';

  const steps = Array.isArray(pipelineSteps) ? pipelineSteps : [];
  const display = steps.length > 0
    ? buildWorkflowRowStepDisplay(pipelineSteps, result, () => ({ status: 'pending' }))
    : [];

  let doneCount = 0;
  let issueCount = 0;
  display.forEach((st) => {
    if (st?.status === 'done') doneCount += 1;
    else if (st?.status === 'failed' || st?.status === 'stopped' || st?.status === 'risk' || st?.status === 'skipped') {
      issueCount += 1;
    }
  });

  const hasFinalOutput = Boolean(String(result.finalImageUrl || '').trim() || result.finalSpaceId);

  if (result.ok === true && issueCount === 0) return 'success';
  if (result.skipped && issueCount > 0 && doneCount === 0) return 'failed';
  if (issueCount > 0 && doneCount > 0) return 'partial';
  if (issueCount > 0 && hasFinalOutput) return 'partial';
  if (!result.ok && doneCount > 0) return 'partial';
  if (!result.ok && hasFinalOutput) return 'partial';
  if (result.ok === true) return 'success';
  return 'failed';
}

/** 条目是否曾检测到较高/高风险侵权（用于条目切换后仍显示标记） */
export function getWorkflowRowInfringementSummary(result, expectedRunId) {
  if (!result || result.inProgress) return null;
  if (expectedRunId != null) {
    if (result.workflowRunId == null || result.workflowRunId !== expectedRunId) {
      return null;
    }
  }
  if (result.infringementHighRisk) {
    return {
      highRisk: true,
      riskLevel: result.infringementRiskLevel || 'high',
      note: result.infringementNote || '',
    };
  }
  const steps = Array.isArray(result.steps) ? result.steps : [];
  const step = steps.find((s) => resolveWorkflowNavId(s) === 'infringementDetection');
  if (step) {
    const highRisk =
      Boolean(step.highRisk) ||
      String(step.riskLevel || '').toLowerCase() === 'high' ||
      step.status === 'skipped' ||
      /高风险|较高侵权|极高侵权|侵权风险/i.test(String(step.note || step.error || ''));
    if (highRisk) {
      return {
        highRisk: true,
        riskLevel: step.riskLevel || 'high',
        note: step.note || step.error || result.error || '',
      };
    }
  }
  if (
    result.skipped &&
    (result.stoppedAtNavId === 'infringementDetection' ||
      /侵权/.test(String(result.error || '')))
  ) {
    return {
      highRisk: true,
      riskLevel: 'high',
      note: result.error || '',
    };
  }
  return null;
}

/** 步骤记录里用于预览跳转的任务 / 批次 ID 与产出地址 */
export function pickWorkflowStepLinkFields(rec) {
  if (!rec || typeof rec !== 'object') {
    return {
      compositeImageUrls: [],
      compositeImageViewUrls: [],
      outputImageUrlDisplay: '',
      viewUrl: '',
      displayUrl: '',
      taskId: '',
      goodsTaskId: '',
      videoTaskId: '',
      copywritingTaskId: '',
      titleTaskId: '',
      listingJobId: '',
      listingJobIds: [],
      infringementBatchId: '',
      batchId: '',
      sourcingBatchKey: '',
      navId: '',
    };
  }
  return {
    compositeImageUrls: Array.isArray(rec.compositeImageUrls) ? rec.compositeImageUrls.filter(Boolean) : [],
    compositeImageViewUrls: Array.isArray(rec.compositeImageViewUrls) ? rec.compositeImageViewUrls : [],
    outputImageUrlDisplay: rec.outputImageUrlDisplay || '',
    viewUrl: rec.viewUrl || rec.displayUrl || '',
    displayUrl: rec.displayUrl || rec.viewUrl || '',
    taskId: rec.taskId || '',
    goodsTaskId: rec.goodsTaskId || '',
    videoTaskId: rec.videoTaskId || '',
    copywritingTaskId: rec.copywritingTaskId || rec.titleTaskId || '',
    titleTaskId: rec.titleTaskId || '',
    listingJobId: rec.listingJobId || '',
    listingJobIds: Array.isArray(rec.listingJobIds) ? rec.listingJobIds : [],
    infringementBatchId: rec.infringementBatchId || '',
    batchId: rec.batchId || rec.infringementBatchId || '',
    sourcingBatchKey: rec.sourcingBatchKey || rec.batchKey || '',
    navId: rec.navId || '',
  };
}

export function findPipelineResultStep(resultSteps, navId, fallbackIndex) {
  if (!Array.isArray(resultSteps)) return null;
  const resolved = resolveWorkflowNavId({ navId });
  const atIndex = Number.isInteger(fallbackIndex) ? resultSteps[fallbackIndex] : null;
  // 优先按位次匹配，避免同 navId 重复步骤取错记录
  if (atIndex && (!resolved || (resolveWorkflowNavId(atIndex) || atIndex.navId) === resolved)) {
    return atIndex;
  }
  if (resolved) {
    const matched = resultSteps.find((s) => resolveWorkflowNavId(s) === resolved);
    if (matched) return matched;
  }
  return atIndex ?? resultSteps[fallbackIndex] ?? null;
}
